/**
 * OfflineHandler middleware
 *
 * If the the app is determined to be offline, the app goes into an offline mode which
 * blocks actions that would cause the next state to be one that requires additional
 * data from network calls.
 *
 * Actions such as stats calls are exempt since they do not affect UI.
 * Actions such as changing stations would be blocked and buffered
 *
 * Once network is restored, the last blocked action will be replayed
 *
 * @module application/sagas/workers/OfflineHandler
 */

import { LOCATION_CHANGE } from 'react-router-redux';

import * as D from '../../deps';

// our local logger instance
const log = D.Logger.child({
  component: 'OfflineHandler',
});

export const blockAction = ({ action, store }) => {
  log.info('Application offline. Blocking action: ' + action.type);
  const { dispatch } = store;
  dispatch(D.AppActions.setLastOfflineAction(action));
  return new Promise((resolve, reject) => {
    dispatch(D.OverlayActions.openOfflineNotification());
    return reject();
  });
};

const blockedActionHashMap = {
  [D.Constants.UseCases.ActionTypes.START_STATION]: true,
  [D.Constants.Playback.ActionTypes.TRACK_THUMBDOWN]: true,
  [D.Constants.Playback.ActionTypes.TRACK_THUMBUP]: true,
  [D.Constants.Playback.ActionTypes.TIRE_OF_TRACK]: true,
  [D.Constants.UseCases.ActionTypes.SKIP_TRACK_FORWARD]: true,
  [D.Constants.UseCases.ActionTypes.REPLAY_TRACK]: true,
  [D.Constants.Playback.ActionTypes.REMOVE_STATION]: true,
  [D.AudioPlayerActions.ENDED]: true,
  [LOCATION_CHANGE]: true,
};

export const checkAction = ({ next, action, store }) => {
  if (blockedActionHashMap[action.type]) {
    const state = store.getState();
    // Session history already loaded in now playing, so switching back and forth okay
    if (
      state.routing &&
      state.routing.locationBeforeTransitions &&
      action.type === LOCATION_CHANGE &&
      D.RouteUtils.isPathInNowPlayingPath(state.routing.locationBeforeTransitions.pathname) &&
      D.RouteUtils.isPathInNowPlayingPath(action.payload.pathname)
    ) {
      return next(action);
    }
    if (D.AppUtils.isApplicationOffline(state)) {
      return Promise.resolve(blockAction({ action, store }));
    }
  }
  return next(action);
};

export default store => next => action => {
  return checkAction({ action, next, store });
};
